import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../utils/api';
import { TYPE_COLORS, TYPE_NAMES_RU } from './AdvancedRackCanvas';
import { Cable, Zap, Wifi, Monitor, ChevronDown, ChevronRight, RefreshCw, Settings } from 'lucide-react';
import { usePatchingStore } from '../../stores/usePatchingStore';
import toast from 'react-hot-toast';

interface RackEquipmentItem {
  id: string;
  name: string;
  equipment_type: string;
  u_height: number;
  u_position: number;
  is_zero_u?: boolean;
}

interface Port {
  id: string;
  name: string;
  port_type: string;
  connector_type?: string;
  status?: string;
  connected_cable_id?: string;
  connected_via?: Array<{
    cable_id: string;
    other_port_id: string | null;
    cable_type: string;
    length_m?: number;
  }>;
  // extra fields from API
  cable_label?: string;
  remote_device?: string;
  remote_port?: string;
  remote_u?: number;
}

interface DevicePortsProps {
  device: RackEquipmentItem;
  dcCode: string;
  workspace: string;
  onTracePort?: (portId: string) => void;
}

// ─── Port type classification ───────────────────────────────────────────────

function getPortGroup(port: Port): 'network' | 'power' | 'management' {
  const name = (port.name || '').toLowerCase();
  const type = (port.port_type || '').toLowerCase();
  const conn = (port.connector_type || '').toLowerCase();

  if (
    type === 'power' || type === 'power-outlet' || type === 'power-port' ||
    conn.includes('iec') || conn.includes('nema') || name === 'pwr' ||
    name.startsWith('power') || name.startsWith('psu')
  ) return 'power';

  if (
    name.includes('ilo') || name.includes('idrac') || name.includes('imes') ||
    name.includes('imm') || name.includes('bmc') ||
    name === 'mgmt' || name === 'management' || name === 'console' ||
    name.startsWith('con') || name.startsWith('mng') ||
    type === 'management' || type === 'console-server-port'
  ) return 'management';

  return 'network';
}

function isOptical(port: Port): boolean {
  const conn = (port.connector_type || '').toLowerCase();
  const name = (port.name || '').toLowerCase();
  return (
    conn.includes('sfp') || conn.includes('qsfp') || conn.includes('cfp') ||
    conn.includes('xfp') || conn.includes('lc') || conn.includes('sc') ||
    conn.includes('mpo') || conn.includes('fiber') || conn.includes('optical') ||
    conn.includes('mmf') || conn.includes('smf') ||
    name.includes('sfp') || name.includes('fiber') || name.startsWith('xe-') ||
    name.startsWith('et-') || name.startsWith('fo')
  );
}

type PortStatus = 'connected' | 'free' | 'reserved' | 'failed';

function getPortStatus(port: Port): PortStatus {
  const status = (port.status || '').toLowerCase();
  if (status === 'decommissioned' || status === 'failed' || status === 'disabled') return 'failed';
  if (status === 'planned' || status === 'staged') return 'reserved';
  if (port.connected_cable_id || (port.connected_via && port.connected_via.length > 0)) return 'connected';
  return 'free';
}

const PORT_STATUS_STYLES: Record<PortStatus, string> = {
  connected: 'border-emerald-500 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/35',
  free:      'border-slate-700  bg-slate-900/50   text-slate-500  hover:border-slate-500 hover:bg-slate-800/50',
  reserved:  'border-amber-500  bg-amber-500/15   text-amber-400  hover:bg-amber-500/25',
  failed:    'border-red-500    bg-red-500/15     text-red-400    hover:bg-red-500/25',
};

const PORT_STATUS_LABEL: Record<PortStatus, string> = {
  connected: 'Подключён',
  free:      'Свободен',
  reserved:  'Зарезервирован',
  failed:    'Неисправен',
};

// ─── Port Tooltip ────────────────────────────────────────────────────────────

function PortTooltip({ port, x, y }: { port: Port; x: number; y: number }) {
  const status = getPortStatus(port);
  const optical = isOptical(port);
  const connectorLabel = port.connector_type
    ? `${optical ? 'Оптика' : 'Медь'} · ${port.connector_type}`
    : optical ? 'Оптический' : 'Медный';

  const W = 240;
  const margin = 10;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = x + 16 + W > vw - margin ? Math.max(margin, x - W - 8) : x + 16;
  const top  = Math.min(y - 20, vh - 160);

  return (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{ left, top, width: W }}
    >
      <div className="bg-slate-900 border border-slate-600 rounded-lg shadow-2xl shadow-black/60 overflow-hidden text-[11px] font-mono">
        {/* Header */}
        <div className="px-3 py-2 flex items-center gap-2 border-b border-slate-800 bg-slate-950/60">
          <span className={`w-2 h-2 rounded-full shrink-0 ${
            status === 'connected' ? 'bg-emerald-400' :
            status === 'reserved'  ? 'bg-amber-400' :
            status === 'failed'    ? 'bg-red-400' : 'bg-slate-600'
          }`} />
          <span className="font-bold text-slate-100">{port.name}</span>
          <span className="ml-auto text-[9px] text-slate-500 uppercase">{PORT_STATUS_LABEL[status]}</span>
        </div>

        <div className="px-3 py-2 space-y-1">
          {/* Connector type */}
          <div className="flex justify-between">
            <span className="text-slate-500">Разъём</span>
            <span className="text-slate-300">{connectorLabel}</span>
          </div>

          {/* Port type */}
          {port.port_type && (
            <div className="flex justify-between">
              <span className="text-slate-500">Тип порта</span>
              <span className="text-slate-400">{port.port_type}</span>
            </div>
          )}

          {/* Connection info */}
          {status === 'connected' && (
            <>
              <div className="border-t border-slate-800 pt-1 mt-1" />
              {port.remote_device && (
                <div className="flex justify-between">
                  <span className="text-slate-500">→ Устройство</span>
                  <span className="text-emerald-300 font-semibold">{port.remote_device}</span>
                </div>
              )}
              {port.remote_port && (
                <div className="flex justify-between">
                  <span className="text-slate-500">→ Порт</span>
                  <span className="text-emerald-300">{port.remote_port}</span>
                </div>
              )}
              {port.remote_u !== undefined && (
                <div className="flex justify-between">
                  <span className="text-slate-500">→ Позиция</span>
                  <span className="text-emerald-300">U{port.remote_u}</span>
                </div>
              )}
              {port.connected_via?.[0]?.cable_type && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Кабель</span>
                  <span className="text-slate-300">{port.connected_via[0].cable_type}
                    {port.connected_via[0].length_m ? ` · ${port.connected_via[0].length_m}м` : ''}</span>
                </div>
              )}
              {port.cable_label && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Метка</span>
                  <span className="text-slate-300">{port.cable_label}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Single Port Dot ─────────────────────────────────────────────────────────

function PortDot({
  port,
  deviceId,
  deviceName,
  onTrace,
}: {
  port: Port;
  deviceId?: string;
  deviceName?: string;
  onTrace?: (portId: string) => void;
}) {
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);
  const status  = getPortStatus(port);
  const optical = isOptical(port);
  const group   = getPortGroup(port);
  const patchingStore = usePatchingStore();

  const handlePortClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (status === 'free' && deviceId && deviceName) {
      if (!patchingStore.isPatchingMode) {
        patchingStore.setPatchingMode(true);
      }
      if (!patchingStore.leftEquipmentId) {
        patchingStore.setLeftEquipment(deviceId);
        patchingStore.toggleLeftPort(port.id);
        toast.success(`Источник: ${deviceName}, порт ${port.name}`);
      } else if (!patchingStore.rightEquipmentId && patchingStore.leftEquipmentId !== deviceId) {
        patchingStore.setRightEquipment(deviceId);
        patchingStore.toggleRightPort(port.id);
        toast.success(`Приёмник: ${deviceName}, порт ${port.name}`);
      } else {
        if (patchingStore.leftEquipmentId === deviceId) {
          patchingStore.toggleLeftPort(port.id);
        } else if (patchingStore.rightEquipmentId === deviceId) {
          patchingStore.toggleRightPort(port.id);
        } else {
          toast.error('Сначала завершите текущую коммутацию или очистите выбор.');
        }
      }
    } else {
      onTrace?.(port.id);
    }
  };

  const sizeClass = group === 'network' ? 'w-[20px] h-[20px]' : 'w-[18px] h-[18px]';

  return (
    <div
      className="relative"
      onMouseEnter={e => setHoverPos({ x: e.clientX, y: e.clientY })}
      onMouseMove={e => setHoverPos({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setHoverPos(null)}
      onClick={handlePortClick}
    >
      {/* Port Shape */}
      <div
        className={`
          ${sizeClass}
          border rounded-sm flex items-center justify-center
          text-[7px] font-bold font-mono cursor-pointer transition-all duration-150
          ${PORT_STATUS_STYLES[status]}
          ${optical ? 'rounded-full' : ''}
        `}
        title={port.name}
      >
        {group === 'power'      ? <Zap    className="w-2.5 h-2.5" /> :
         group === 'management' ? <Monitor className="w-2.5 h-2.5" /> :
         optical                ? <span className="text-[7px]">◇</span> :
                                  null}
      </div>

      {/* Tooltip */}
      {hoverPos && (
        <PortTooltip port={port} x={hoverPos.x} y={hoverPos.y} />
      )}
    </div>
  );
}

// ─── Port Group Row ───────────────────────────────────────────────────────────

function PortGroupRow({
  label,
  icon,
  ports,
  onTrace,
  deviceId,
  deviceName,
}: {
  label: string;
  icon: React.ReactNode;
  ports: Port[];
  deviceId?: string;
  deviceName?: string;
  onTrace?: (portId: string) => void;
}) {
  if (ports.length === 0) return null;

  const connected = ports.filter(p => getPortStatus(p) === 'connected').length;

  return (
    <div className="flex items-start gap-2 py-0.5">
      {/* Label */}
      <div className="flex items-center gap-1 shrink-0 w-24">
        {icon}
        <span className="text-[9px] text-slate-500 uppercase tracking-wider">{label}</span>
        <span className="text-[9px] text-slate-600 ml-auto">{connected}/{ports.length}</span>
      </div>
      {/* Ports grid */}
      <div className="flex flex-wrap gap-1">
        {ports.map(p => (
          <PortDot key={p.id} port={p} onTrace={onTrace} deviceId={deviceId} deviceName={deviceName} />
        ))}
      </div>
    </div>
  );
}

// ─── Device Row ───────────────────────────────────────────────────────────────

function DeviceConnectivityRow({
  device,
  dcCode,
  workspace,
  onTrace,
}: DevicePortsProps & { onTrace?: (portId: string) => void }) {
  const [expanded, setExpanded] = useState(true);

  const { data: ports = [], isLoading } = useQuery<Port[]>({
    queryKey: ['devicePorts', device.id, workspace],
    queryFn: () => api.get(`/api/equipment/${device.id}/ports`, { params: { workspace } }).then(r => r.data),
    staleTime: 30_000,
  });

  const typeColor = TYPE_COLORS[device.equipment_type] || '#64748b';
  const typeName  = TYPE_NAMES_RU[device.equipment_type] || device.equipment_type;

  const networkPorts    = ports.filter(p => getPortGroup(p) === 'network');
  const powerPorts      = ports.filter(p => getPortGroup(p) === 'power');
  const managementPorts = ports.filter(p => getPortGroup(p) === 'management');

  const connectedTotal = ports.filter(p => getPortStatus(p) === 'connected').length;

  return (
    <div className="border border-slate-800 rounded-lg overflow-hidden">
      {/* Device Header */}
      <button
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-900/60 transition text-left"
        style={{ borderLeft: `3px solid ${typeColor}` }}
        onClick={() => setExpanded(v => !v)}
      >
        {expanded ? <ChevronDown className="w-3 h-3 text-slate-500 shrink-0" /> : <ChevronRight className="w-3 h-3 text-slate-500 shrink-0" />}
        <span className="font-bold text-[11px] text-slate-100 flex-1 truncate">{device.name}</span>
        <span className="text-[9px] text-slate-500 shrink-0">{typeName}</span>
        <span className="text-[9px] text-slate-600 shrink-0 ml-1">U{device.u_position}</span>
        {/* Port summary badge */}
        <span className={`ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full border shrink-0 ${
          ports.length === 0
            ? 'text-slate-600 border-slate-800'
            : connectedTotal === ports.length
            ? 'text-emerald-400 border-emerald-800 bg-emerald-950/30'
            : connectedTotal === 0
            ? 'text-slate-500 border-slate-700'
            : 'text-amber-400 border-amber-800 bg-amber-950/20'
        }`}>
          {isLoading ? '…' : `${connectedTotal}/${ports.length}`}
        </span>
      </button>

      {/* Port Panel */}
      {expanded && (
        <div className="px-3 py-2 bg-slate-900/30 space-y-2 border-t border-slate-800/50">
          {isLoading ? (
            <div className="flex items-center gap-2 text-[10px] text-slate-500 py-1">
              <RefreshCw className="w-3 h-3 animate-spin" />
              Загрузка портов…
            </div>
          ) : ports.length === 0 ? (
            <div className="flex items-center gap-2 py-2">
              <span className="text-[10px] text-slate-600">Порты не настроены</span>
              <Link to={`/${dcCode}/equipment/${device.id}`} className="text-[9px] text-cyan-500 hover:text-cyan-300 flex items-center gap-1 ml-auto transition">
                <Settings className="w-3 h-3" />
                Настроить
              </Link>
            </div>
          ) : (
            <>
              <PortGroupRow label="Сеть" icon={<Wifi className="w-3 h-3 text-emerald-400" />} ports={networkPorts} onTrace={onTrace} deviceId={device.id} deviceName={device.name} />
              <PortGroupRow label="Питание" icon={<Zap className="w-3 h-3 text-yellow-400" />} ports={powerPorts} onTrace={onTrace} deviceId={device.id} deviceName={device.name} />
              <PortGroupRow label="Управление" icon={<Monitor className="w-3 h-3 text-indigo-400" />} ports={managementPorts} onTrace={onTrace} deviceId={device.id} deviceName={device.name} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function ConnectivityLegend() {
  return (
    <div className="flex flex-wrap gap-3 text-[9px] text-slate-500 px-3 py-2 border-b border-slate-800 bg-slate-950/60 font-mono">
      {([
        ['connected', 'Подключён',     'bg-emerald-500'],
        ['free',      'Свободен',      'bg-slate-600'],
        ['reserved',  'Зарезервирован','bg-amber-500'],
        ['failed',    'Неисправен',    'bg-red-500'],
      ] as const).map(([, label, cls]) => (
        <span key={label} className="flex items-center gap-1">
          <span className={`w-2.5 h-2.5 rounded-sm border ${cls} opacity-70`} />
          {label}
        </span>
      ))}
      <span className="flex items-center gap-1 ml-2 border-l border-slate-800 pl-2">
        <span className="w-3 h-3 rounded-full border border-slate-600 flex items-center justify-center text-[7px] text-slate-500">◇</span>
        Оптика
      </span>
      <span className="flex items-center gap-1">
        <span className="w-3 h-3 rounded-sm border border-slate-600" />
        Медь
      </span>
      <span className="flex items-center gap-1">
        <Monitor className="w-3 h-3 text-indigo-400" />
        Управление
      </span>
    </div>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────

interface RackConnectivityViewProps {
  equipmentList: RackEquipmentItem[];
  dcCode: string;
  rackId: string;
  workspace: string;
  onTracePort?: (portId: string) => void;
}

export function RackConnectivityView({
  equipmentList,
  dcCode,
  rackId,
  workspace,
  onTracePort,
}: RackConnectivityViewProps) {
  // Sort by U position descending (top → bottom, like the rack)
  const sortedEquipment = [...equipmentList]
    .filter(eq => !eq.is_zero_u)
    .sort((a, b) => b.u_position - a.u_position);

  if (sortedEquipment.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
        <Cable className="w-8 h-8 text-slate-700" />
        <p className="text-sm text-slate-500">Стойка пуста.</p>
        <p className="text-xs text-slate-600">Добавьте оборудование для просмотра коммутации.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-3 py-2 border-b border-slate-800 bg-slate-950/80 flex items-center gap-2 shrink-0">
        <Cable className="w-3.5 h-3.5 text-emerald-400" />
        <span className="text-[11px] font-bold text-slate-200 uppercase tracking-wider font-mono">
          Коммутация — {dcCode.toUpperCase()} / Стойка #{rackId}
        </span>
        <span className="ml-auto text-[9px] text-slate-600 font-mono">{sortedEquipment.length} устройств</span>
      </div>

      <ConnectivityLegend />

      {/* Device list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5 font-mono text-xs">
        {sortedEquipment.map(device => (
          <DeviceConnectivityRow
            key={device.id}
            device={device}
            dcCode={dcCode}
            workspace={workspace}
            onTrace={onTracePort}
          />
        ))}
      </div>
    </div>
  );
}
